import dayjs from '../../utils/dayjs';
import { Badge, BlockStack, Box, Button, Card, Icon, IndexTable, InlineStack, Link, Text } from '@shopify/polaris';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import helpers from 'helpers/index';
import Pagination from 'components/pagination';

import { AdjustIcon, BillFilledIcon, CashDollarIcon, EditIcon, InfoIcon, RefreshIcon, TaxFilledIcon } from '@shopify/polaris-icons';
import __ from 'languages/index';
import {
  TThaiHDonLabel,
  useMutateGetCurrentInvoiceStatus,
  useGetInvoicesError,
  TypedInvoices,
  useMutateReExportInvoices,
  ENUM_INVOICE_STATUS,
} from 'queries/invoices.query';
import SimpleFilter from 'components/simpleFilter';
import { useNotification } from 'context/NotificationContext';

export default function InvoiceSignedError({ currentInvoiceCallback }: { currentInvoiceCallback: (invoiceID) => void }) {
  const rootSearchPath = window.location.search;
  let StringQuery: any = helpers.ExtractUrl(rootSearchPath) || false;

  const initialQuery = useMemo(
    () => ({
      query: '',
      page: 1,
      limit: 200,
      sort: 'createdAt:desc',
    }),
    []
  );

  const [mainQuery, setMainQuery] = useState<any>({
    ...initialQuery,
    ...StringQuery,
  });

  const { data, isFetching: loading } = useGetInvoicesError(mainQuery);

  const [entities, setEntities] = useState<TypedInvoices[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [sum, setSum] = useState({ count: 0, TgThTien: 0, TgTThue: 0, TgTTTBSo: 0 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 800px)');
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      const matches = 'matches' in event ? event.matches : mediaQuery.matches;
      setIsMobile(matches);
    };

    handleChange(mediaQuery);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange as (event: MediaQueryListEvent) => void);
    } else {
      mediaQuery.addListener(handleChange as (event: MediaQueryListEvent) => void);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange as (event: MediaQueryListEvent) => void);
      } else {
        mediaQuery.removeListener(handleChange as (event: MediaQueryListEvent) => void);
      }
    };
  }, []);

  useEffect(() => {
    if (data) {
      let { body, totalItems } = data;
      let { records, sum } = body;
      setEntities(records);
      setTotalItems(totalItems);
      setSum(sum);
    }
  }, [data]);

  const onChangePagination = useCallback((numPage: number, limit: number) => {
    setMainQuery((prevMainQuery) => ({ ...prevMainQuery, page: numPage, limit: limit }));
  }, []);

  useEffect(() => {
    let buildURLSearch = helpers.buildEndUrl(mainQuery);
    if (window.location.search !== buildURLSearch) {
      window.history.replaceState(null, 'exportedError', '/invoices/exportedError' + buildURLSearch);
    }
  }, [mainQuery]);

  const filterCallback = useCallback(
    (value: any) => {
      if (value === false) {
        return setMainQuery(initialQuery);
      }
      setMainQuery((oldValue) => ({
        ...oldValue,
        ...value,
      }));
    },
    [initialQuery]
  );

  /** lấy status của hóa đơn hiện tại */
  const { mutateAsync: fetchCurrentInvoiceStatus } = useMutateGetCurrentInvoiceStatus();
  const [currentInvoiceDetailReload, setCurrentInvoiceDetailReload] = useState(null);
  const fetchCurrentInvoiceStatusCallback = useCallback(
    async (invoiceID) => {
      setCurrentInvoiceDetailReload(invoiceID);
      try {
        await fetchCurrentInvoiceStatus(invoiceID);
      } catch (e) {}
      setCurrentInvoiceDetailReload(null);
    },
    [entities]
  );
  /** xuất hóa đơn thay thế */
  const { addNotification } = useNotification();
  const { mutateAsync: reExportInvoice } = useMutateReExportInvoices();
  const [currentInvoiceReExport, setCurrentInvoiceReExport] = useState(null);
  const reExportInvoiceCallback = useCallback(
    async (invoiceID) => {
      setCurrentInvoiceReExport(invoiceID);
      try {
        await reExportInvoice(invoiceID);
        addNotification('info', 'Đã xuất hóa đơn thay thế!');
      } catch (e) {}
      setCurrentInvoiceReExport(null);
    },
    [entities]
  );

  const rowMarkup = useMemo(
    () =>
      entities.map((invoices, index) => {
        const invoiceId = invoices.invoice_id;
        const tenTThaiHDon = TThaiHDonLabel[invoices.maTThaiHDon] || '--';
        const taxType = invoices.TaxType || 'DEFAULT';
        const createdAt = invoices.createdAt ? dayjs(new Date(Number(invoices.createdAt))).format('HH:mm DD/MM/YYYY') : '--';
        const signedAt = invoices.signedAt ? dayjs(new Date(Number(invoices.signedAt))).format('HH:mm DD/MM/YYYY') : '--';
        const signedBy = (invoices as any)?.user?.display_name || (invoices as any)?.author_data?.display_name || '--';
        const pdfUrl = process.env.REACT_APP_BACKEND_URL + `/invoices/view_pdf/${invoices.maTraCuu}`;

        return (
          <IndexTable.Row id={invoiceId} key={invoiceId} position={index}>
            <IndexTable.Cell>
              <Text variant="bodyMd" fontWeight="bold" as="span">
                <Link url={pdfUrl || '#'} target="_blank">
                  {invoices.shDon || '--'}
                </Link>
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>{signedBy}</IndexTable.Cell>
            <IndexTable.Cell>{signedAt}</IndexTable.Cell>
            <IndexTable.Cell>
              {invoices.ghiChuCQT ? (
                <Text as="p" tone="critical">
                  {invoices.ghiChuCQT}
                </Text>
              ) : null}

              {invoices.invoice_status !== ENUM_INVOICE_STATUS.CANCEL ? (
                <BlockStack>
                  <Text as="p" tone="critical">
                    {tenTThaiHDon}
                  </Text>
                  <InlineStack gap={'200'} wrap={false}>
                    <Button variant="monochromePlain" size="slim" icon={EditIcon} onClick={() => currentInvoiceCallback(invoices.invoice_id)}>
                      Edit
                    </Button>

                    <Button
                      icon={RefreshIcon}
                      // disabled={!!invoices.mccqt}
                      variant="monochromePlain"
                      size="slim"
                      loading={currentInvoiceDetailReload === invoices.invoice_id}
                      onClick={() => fetchCurrentInvoiceStatusCallback(invoices.invoice_id)}
                    >
                      Reload
                    </Button>

                    <Button
                      icon={AdjustIcon}
                      // disabled={!!invoices.invoice_parent}
                      variant="monochromePlain"
                      size="slim"
                      loading={currentInvoiceReExport === invoices.invoice_id}
                      onClick={() => reExportInvoiceCallback(invoices.invoice_id)}
                    >
                      Xuất thay thế
                    </Button>
                  </InlineStack>
                </BlockStack>
              ) : null}
            </IndexTable.Cell>
            <IndexTable.Cell>{invoices.invoice_parent_data ? invoices.invoice_parent_data?.shDon : '--'}</IndexTable.Cell>
            <IndexTable.Cell>
              <Badge size="small" tone={taxType === 'COMPANY' ? 'critical' : 'info'}>
                {__('tax_type_' + taxType)}
              </Badge>
            </IndexTable.Cell>

            <IndexTable.Cell>{invoices.NMua_HVTNMHang || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_CCCDan || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_Ten || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_MST || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_SDThoai || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_DCTDTu || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_DChi || '--'}</IndexTable.Cell>
            <IndexTable.Cell>
              <Text as="span" alignment="end" numeric>
                {helpers.formatNumberWithCommas(invoices.TgTThue)}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Text as="span" alignment="end" numeric>
                {helpers.formatNumberWithCommas(invoices.TgThTien)}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Text as="span" alignment="end" numeric>
                {helpers.formatNumberWithCommas(invoices.TgTTTBSo)}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>{createdAt}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.maTraCuu || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.MTChieu || '--'}</IndexTable.Cell>
          </IndexTable.Row>
        );
      }),
    [entities, currentInvoiceDetailReload, currentInvoiceReExport]
  );

  return (
    <BlockStack gap="400">
      <BlockStack gap="200">
        <InlineStack gap={isMobile ? '200' : '400'} wrap>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <span>
                    <Icon source={InfoIcon} tone="subdued" />
                  </span>
                  <Text as="span" tone="subdued">
                    Tổng số dữ liệu
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(sum?.count || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <span>
                    <Icon source={CashDollarIcon} tone="subdued" />
                  </span>
                  <Text as="span" tone="subdued">
                    Tổng tiền
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(sum?.TgTTTBSo || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <span>
                    <Icon source={TaxFilledIcon} tone="subdued" />
                  </span>
                  <Text as="span" tone="subdued">
                    Tổng thuế
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(sum?.TgTThue || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <span>
                    <Icon source={BillFilledIcon} tone="subdued" />
                  </span>
                  <Text as="span" tone="subdued">
                    Tổng thành tiền
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(sum?.TgThTien || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
        </InlineStack>
      </BlockStack>
      <Box paddingBlockEnd="400">
        <Card padding="0">
          <div className="invoice_managers_wrapper">
            <SimpleFilter
              loading={loading}
              onCallback={filterCallback}
              sortField={[
                { label: 'Tổng tiền', field: 'TgTTTBSo' },
                { label: 'Ngày ký', field: 'signedAt' },
                // { label: 'Ngày khởi hành', field: 'trip_manifest.departure_date' },
              ]}
              options={[]}
              // defaultSort="trip_manifest.departure_date:desc"
            />
            <IndexTable
              resourceName={{
                singular: 'invoice',
                plural: 'invoices',
              }}
              itemCount={entities.length}
              hasZebraStriping
              selectable={false}
              emptyState={
                <Text as="p" tone="subdued">
                  {loading ? 'Đang tải dữ liệu...' : 'Không có hóa đơn đã ký.'}
                </Text>
              }
              headings={[
                { title: 'Số HĐ' },
                { title: 'Người ký' },
                { title: 'Ngày ký' },
                { title: 'T.Trạng CQT' },
                { title: 'Số thay thế' },
                { title: 'Loại thuế' },
                { title: 'Tên người mua' },
                { title: 'CCCD' },
                { title: 'Tên đơn vị' },
                { title: 'MST' },
                { title: 'SĐT' },
                { title: 'Email' },
                { title: 'Địa chỉ' },
                { title: 'Thuế', alignment: 'end' },
                { title: 'Thành tiền', alignment: 'end' },
                { title: 'Tổng tiền', alignment: 'end' },
                { title: 'Ngày tạo' },
                { title: 'Mã tra cứu' },
                { title: 'Mã tham chiếu' },
              ]}
            >
              {rowMarkup}
            </IndexTable>
          </div>
        </Card>
      </Box>

      {totalItems > 0 ? (
        <Pagination
          TotalRecord={totalItems}
          onChange={onChangePagination}
          pageSize={Number(mainQuery.limit)}
          activeCurrentPage={Number(mainQuery.page)}
        />
      ) : null}
    </BlockStack>
  );
}

